import React from "react";

/**
 * Flash styles for validation highlighting across AG Grid cells.
 * Used to draw user attention to cells that need action (e.g., missing values).
 */

/**
 * Returns inline styles for a flashing validation effect.
 * Creates an oval/pill shape with a pulsing red glow.
 *
 * @param isFlashing - Whether the flash effect should be active
 * @returns React.CSSProperties object for inline styles
 */
export function getFlashStyles(isFlashing: boolean): React.CSSProperties {
  if (!isFlashing) {
    return { width: "100%" };
  }

  return {
    width: "100%",
    height: "80%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #dc2626", // red-600
    borderRadius: "9999px", // Full oval/pill shape
    animation: "flash-pulse 0.5s ease-in-out infinite",
    boxShadow: "0 0 0px 0px rgba(239, 68, 68, 0.6)",
    marginTop: "0px",
  };
}

/**
 * Flash for fixed-size inputs outside AG Grid: border, glow and pulse only, so
 * the input keeps its own height and corners (getFlashStyles reshapes to a pill).
 */
export function getInputFlashStyles(
  isFlashing: boolean,
): React.CSSProperties | undefined {
  if (!isFlashing) return undefined;
  return {
    border: "1px solid #dc2626", // red-600
    boxShadow: "0 0 2px 2px rgba(239, 68, 68, 0.6)",
    animation: "flash-pulse 0.5s ease-in-out infinite",
  };
}

/**
 * Keyframe animation component for the flash pulse effect.
 * Renders a <style> tag with the animation definition.
 * Only renders when isFlashing is true.
 *
 * @param isFlashing - Whether to render the animation styles
 */
export const FlashKeyframes: React.FC<{ isFlashing: boolean }> = ({
  isFlashing,
}) => {
  if (!isFlashing) {
    return null;
  }

  return (
    <style>{`
      @keyframes flash-pulse {
        0%, 100% {
          box-shadow: 0 0 1px 2px rgba(252, 106, 8, 0.6);
        }
        50% {
          box-shadow: 0 0 2px 3px rgba(239, 68, 68, 0.8);
        }
      }
    `}</style>
  );
};
